import { ErrorOutline, Home, ArrowBack } from "@mui/icons-material";
import { Box, Button, Container, Stack, Typography } from "@mui/joy";
import { useNavigate } from "react-router";

import { RoutePaths } from "~/common/constants/route-paths";

export interface ErrorPageProps {
  message?: string;
  details?: string;
  stack?: string;
  showHomeButton?: boolean;
}

/**
 * General error page component
 *
 * Displays when an unexpected error occurs during routing or rendering
 * Shows error message and provides navigation options
 */
export function ErrorPage({
  message = "Щось пішло не так",
  details,
  stack,
  showHomeButton = true,
}: ErrorPageProps) {
  const navigate = useNavigate();

  const handleGoBack = () => {
    void navigate(-1);
  };

  const handleGoHome = () => {
    void navigate(RoutePaths.HOME);
  };

  return (
    <Container
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
        py: 4,
      }}
    >
      <Box
        sx={{
          maxWidth: 600,
          width: "100%",
          textAlign: "center",
        }}
      >
        <Stack spacing={3} alignItems="center">
          {/* Error Icon */}
          <Box
            sx={{
              width: 120,
              height: 120,
              borderRadius: "50%",
              bgcolor: "danger.softBg",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <ErrorOutline
              sx={{
                fontSize: 64,
                color: "danger.solidBg",
              }}
            />
          </Box>

          {/* Error Message */}
          <Stack spacing={1}>
            <Typography level="h1" fontSize={{ xs: 32, sm: 40 }}>
              Упс!
            </Typography>
            <Typography level="h4" color="neutral">
              {message}
            </Typography>
            {details && (
              <Typography level="body-md" color="neutral">
                {details}
              </Typography>
            )}
          </Stack>

          {/* Error Stack (only in development) */}
          {stack && import.meta.env.DEV && (
            <Box
              sx={{
                p: 2,
                bgcolor: "neutral.softBg",
                borderRadius: "sm",
                maxWidth: "100%",
                width: "100%",
                height: "200px",
                overflow: "auto",
                textAlign: "left",
              }}
            >
              <Typography
                level="body-sm"
                fontFamily="monospace"
                sx={{ whiteSpace: "pre-wrap" }}
              >
                {stack}
              </Typography>
            </Box>
          )}

          {/* Action Buttons */}
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            sx={{ width: "100%", maxWidth: 400 }}
          >
            <Button
              variant="outlined"
              color="neutral"
              startDecorator={<ArrowBack />}
              onClick={handleGoBack}
              fullWidth
            >
              Назад
            </Button>
            {showHomeButton && (
              <Button
                variant="solid"
                color="primary"
                startDecorator={<Home />}
                onClick={handleGoHome}
                fullWidth
              >
                На головну
              </Button>
            )}
          </Stack>

          {/* Help Text */}
          <Typography level="body-sm" color="neutral">
            Якщо проблема повторюється, зверніться до підтримки
          </Typography>
        </Stack>
      </Box>
    </Container>
  );
}
